import React, { useEffect, useMemo, useState } from 'react'
import LabelAutoComplete from '../components/LabelAutoComplete'
import LabelOnly from '../components/LabelOnly'
import { MonthlyPVBarGraph } from '../components/graph/MonthlyPVBarGraph'
import { RevenueBarGraph } from '../components/graph/RevenueBarGraph'
import TOUHeatMap from '../components/graph/TOUHeatMap'
import { useLazyQuery, useQuery } from '@apollo/client'
import {
  findAllBuyTOUGQL,
  findOneBuyTOUGQL,
  findPVDataGQL,
  getRECGQL,
  getHolidaysGQL,
  getSMPGQL,
} from '../gql/PurchasePlans.gql'
import {
  MonthChip,
  MonthChipText,
  MonthChipWrapper,
} from './PurchasePlans.styled'
import MonthLineGraph2 from '../components/graph/MonthLineGraph2'
import {
  ContentWrapper,
  Body,
  FirstRow,
  GraphBody,
  GraphWrapper,
  Header,
  Item,
  ItemWrapper,
  Left,
  PlanSummaryText,
  PvChartBox,
  PvTableBox,
  PvTableChartRow,
  Right,
  SinglePriceBox,
  Table,
  TableCell,
  TableCell2,
  TableCell3,
  TableRow,
  Title,
} from './EconomicPV.styled'
import { calLoadTimeSum, divTimeData } from '../util/algorithm/classifyTimeData'
import {
  ILoadTimeSum,
  IMonthNum,
  ITimeDataItem,
} from '../util/algorithm/algoInterface'
import { calMonthRepPattern } from '../util/algorithm/calRepPattern'
import { seasonMapping } from '../util/SeasonMapping'
import {
  calMonthlyChargeAsUsage,
  sumMonthlyLoadTime,
} from '../util/algorithm/calCharge'
import {
  IMonthNumPlusIMonthNum,
  IMonthNumMinusIMonthNum,
  convertMonthNumToArray,
} from '../util/algorithm/calExCharge'
import LabelUnit from '../components/LabelUnit'
import { findAllPVcusGQL } from '../gql/CustomerPV.gql'
import {
  calculateMonthlyPvRecRevenue,
  calculateMonthlyPvPreviousRevenue,
  getPvRevenueContractType,
  mapSmpDataToValue,
  normalizeSmpOptForPvContract,
} from '../util/algorithm/pvRevenue'

export const EconomicPV = () => {
  const [PageData, setPageData] = useState<any>({
    cust: '',
    plans: '',
  })

  const { data: originPVCusList } = useQuery(findAllPVcusGQL)
  const [PVCusList, setPVCusList] = useState<any>()

  const { data: originPurchasePlansList } = useQuery(findAllBuyTOUGQL)
  const [PurchasePlansList, setPurchasePlansList] = useState<any>()

  const [findPVData] = useLazyQuery(findPVDataGQL)
  //PV 데이터
  const [PVData, setPVData] = useState<any>()
  const [findOnePurchasePlanData] = useLazyQuery(findOneBuyTOUGQL)
  //구매요금제 데이터
  const [BuyTOUData, setBuyTOUData] = useState<any>()

  //공휴일 데이터
  const [holidaysData, setHolidaysData] = useState<any>()
  const [findHolidaysData] = useLazyQuery(getHolidaysGQL)

  // [추가] SMP 데이터 상태 및 쿼리
  const [smpData, setSmpData] = useState<any[]>()
  const [findSMPData] = useLazyQuery(getSMPGQL)
  const [recData, setRecData] = useState<any[]>()
  const [findRECData] = useLazyQuery(getRECGQL)

  const [usingPvData, setUsingPvData] = useState<ITimeDataItem[]>([])

  // 태양광발전량 표 데이터 (월별 최저중간최대부하)
  const [monthlyPV, setMonthlyPV] = useState<ILoadTimeSum>()

  //수익 상세보기 - 기존수익 계산
  const [beforeCharge, setBeforeCharge] = useState<any>()

  //수익 상세보기 - ESS 사업자 거래후 계산
  const [afterCharge, setAfterCharge] = useState<any>()
  const [monthlyRecRevenue, setMonthlyRecRevenue] = useState<IMonthNum>()
  const shouldShowRecRevenue = BuyTOUData?.recOpt === 'energyOnly'
  const isSingleTimePlan =
    BuyTOUData?.planType === 'flat' ||
    BuyTOUData?.timeTableOpt === 'flat' ||
    BuyTOUData?.timeTableOpt === 'single'
  const singleUnitPrice = BuyTOUData?.chargeUnitResult?.spr?.min || 0
  const recContractText =
    BuyTOUData?.recOpt === 'energyAndRec'
      ? 'REC 포함: 전력량과 REC 고정가격계약'
      : 'REC 미포함: 전력량만 구매'
  const filteredPurchasePlansList = useMemo(() => {
    if (!PurchasePlansList) return []
    if (!PageData.cust?._id) return PurchasePlansList

    return PurchasePlansList.filter(
      (plan: any) => plan.custId === PageData.cust._id
    )
  }, [PageData.cust?._id, PurchasePlansList])
  const afterChargeWithRec = useMemo(() => {
    if (!afterCharge) return undefined
    if (!shouldShowRecRevenue || !monthlyRecRevenue) return afterCharge

    return IMonthNumPlusIMonthNum(afterCharge, monthlyRecRevenue)
  }, [afterCharge, monthlyRecRevenue, shouldShowRecRevenue])

  //수익 상세보기 - 고객 이익 계산
  const custProfit = useMemo(() => {
    if (beforeCharge && afterChargeWithRec) {
      return IMonthNumMinusIMonthNum(afterChargeWithRec, beforeCharge) as any
    }
  }, [beforeCharge, afterChargeWithRec])

  // [추가] 총합 계산 로직 (발전량)
  const totalPV = useMemo(() => {
    if (!monthlyPV) return { min: 0, mid: 0, max: 0, all: 0 }
    const sums = Object.values(monthlyPV).reduce(
      (acc, curr) => {
        acc.min += curr.min
        acc.mid += curr.mid
        acc.max += curr.max
        return acc
      },
      { min: 0, mid: 0, max: 0 }
    )
    return { ...sums, all: sums.min + sums.mid + sums.max }
  }, [monthlyPV])

  const monthlyPVBarData = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => {
        const month = index + 1
        if (!monthlyPV) return 0
        return (
          monthlyPV[month].min + monthlyPV[month].mid + monthlyPV[month].max
        )
      }),
    [monthlyPV]
  )

  // [추가] 총합 계산 로직 (수익)
  const totalProfit = useMemo(() => {
    const sumObj = (obj: any) => {
      if (!obj) return 0
      return Object.values(obj).reduce(
        (acc: number, curr: any) => acc + curr,
        0
      )
    }
    return {
      before: sumObj(beforeCharge),
      after: sumObj(afterCharge),
      rec: sumObj(monthlyRecRevenue),
      cust: sumObj(custProfit),
    }
  }, [beforeCharge, afterCharge, monthlyRecRevenue, custProfit])

  // 공휴일 및 SMP 데이터 가져오기 (디버깅 추가됨)
  useEffect(() => {
    if (BuyTOUData) {
      // 1. 공휴일 가져오기
      if (BuyTOUData.holidayYear) {
        findHolidaysData({
          variables: { val: String(BuyTOUData.holidayYear) },
        }).then((result: any) => {
          if (result.data) {
            const holidayResult = Object.values(result.data)[0] as any
            setHolidaysData(holidayResult.data)
          }
        })
      }

      // 2. SMP 데이터 가져오기 (로그 집중 추가)
      if (BuyTOUData.smpYear) {
        const yearStr = String(BuyTOUData.smpYear)
        console.log(`🚀 [SMP 요청 시작] 연도: ${yearStr}`)

        findSMPData({
          variables: { val: yearStr },
        })
          .then((result: any) => {
            console.log('📦 [SMP 서버 응답]:', result)

            // 데이터가 있는지 단계별 확인
            if (!result.data) {
              console.error(
                '❌ result.data가 없습니다. 네트워크 탭 응답을 확인하세요.'
              )
              return
            }

            const smpResult = result.data.findSmpByYear

            if (!smpResult) {
              // 여기가 문제일 확률이 높음 (DB에서 못 찾음)
              console.warn(
                `⚠️ 서버가 null을 반환했습니다. DB에 { year: "${yearStr}" } 데이터가 있는지 확인하세요.`
              )
              setSmpData([]) // 빈 배열이라도 넣어줌
            } else if (smpResult.data) {
              // 정상 케이스
              console.log(`✅ 데이터 로드 성공! (${smpResult.data.length}개)`)
              setSmpData(smpResult.data)
            } else {
              console.warn('⚠️ 데이터 구조가 이상합니다:', smpResult)
            }
          })
          .catch((e) => {
            console.error('🔥 [SMP 요청 실패] 에러 내용:', e)
          })
      } else {
        console.warn(
          '⚠️ BuyTOUData에 smpYear(연도) 정보가 없습니다:',
          BuyTOUData
        )
      }
    }
  }, [BuyTOUData])

  useEffect(() => {
    if (BuyTOUData?.recYear && BuyTOUData.recYear !== '') {
      findRECData({
        variables: { val: String(BuyTOUData.recYear) },
      }).then((result: any) => {
        if (result.data) {
          const recResult = Object.values(result.data)[0] as any[]
          setRecData(recResult?.[0]?.data || [])
        }
      })
    } else {
      setRecData([])
    }
  }, [BuyTOUData?.recYear])

  // PV 데이터 가공 및 선택
  useEffect(() => {
    if (PVData && BuyTOUData) {
      const tmpData = divTimeData(PVData, 1000) // W -> kW 변환
      const pvOpt =
        PageData.cust?.pvOpt || BuyTOUData?.cust?.pvOpt || 'realTime'

      switch (pvOpt) {
        case 'aver':
          const monthRepPatternAver = calMonthRepPattern(tmpData, 'aver')
          setUsingPvData(monthRepPatternAver.repData)
          break
        case 'mid':
          const monthRepPatternMid = calMonthRepPattern(tmpData, 'mid')
          setUsingPvData(monthRepPatternMid.repData)
          break
        case 'mode':
          const monthRepPatternMode = calMonthRepPattern(tmpData, 'mode')
          setUsingPvData(monthRepPatternMode.repData)
          break
        case 'realTime':
        default:
          setUsingPvData(tmpData)
          break
      }
    }
  }, [BuyTOUData, PVData, PageData.cust?.pvOpt])

  // 월별 발전량 집계 (monthlyPV)
  useEffect(() => {
    if (BuyTOUData && usingPvData && holidaysData) {
      setMonthlyPV(
        calLoadTimeSum(
          usingPvData,
          seasonMapping,
          holidaysData,
          BuyTOUData.touPeriodResult
        ).loadTimeSumResult
      )
    }
  }, [BuyTOUData, usingPvData, holidaysData])

  // 기존 수익(Before) 계산
  useEffect(() => {
    if (!monthlyPV || !BuyTOUData || usingPvData.length === 0) return

    const monthlySumPV = sumMonthlyLoadTime(monthlyPV)
    const revenueCustomer = PageData.cust?._id ? PageData.cust : BuyTOUData.cust
    const revenueContractType = getPvRevenueContractType(revenueCustomer)
    const smpOpt = normalizeSmpOptForPvContract(
      revenueContractType,
      revenueContractType === 'marketSmp' ? BuyTOUData.smpOpt : undefined
    )
    const selectedSmpData = mapSmpDataToValue(smpData || [], smpOpt)
    const revenue = calculateMonthlyPvPreviousRevenue({
      customer: revenueCustomer,
      usingPvData,
      monthlyPVSum: monthlySumPV,
      smpData: selectedSmpData,
      recData,
      recYear: BuyTOUData.recYear,
      recRegion: BuyTOUData.recRegion,
    })

    if (revenue) {
      setBeforeCharge(revenue)
    } else {
      // ▼▼▼ [디버깅] 상세 데이터 상태 로그 ▼▼▼
      console.warn('⚠️ [데이터 대기 중] 다음 4가지 중 누락된 값을 찾으세요:')

      console.log(
        `1. monthlyPV (월별 발전량 합계):`,
        monthlyPV ? '✅ 있음' : '❌ 없음 (null/undefined)'
      )
      console.log(
        `2. BuyTOUData (요금제 정보):`,
        BuyTOUData ? '✅ 있음' : '❌ 없음'
      )

      // smpData가 배열인지, 비어있는지 확인
      if (Array.isArray(smpData) && smpData.length > 0) {
        console.log(`3. smpData (SMP 데이터): ✅ 있음 (${smpData.length}개)`)
      } else {
        console.log(`3. smpData (SMP 데이터): ❌ 없음 또는 빈 배열`, smpData)
      }

      // usingPvData가 배열인지, 비어있는지 확인
      if (Array.isArray(usingPvData) && usingPvData.length > 0) {
        console.log(
          `4. usingPvData (시간별 PV 데이터): ✅ 있음 (${usingPvData.length}개)`
        )
      } else {
        console.log(
          `4. usingPvData (시간별 PV 데이터): ❌ 없음 (${usingPvData?.length})`
        )
      }

      // 추가 힌트: 요금제에 연도가 없으면 SMP 요청 자체를 안 함
      if (BuyTOUData && !BuyTOUData.smpYear) {
        console.error(
          '🚨 [치명적 문제] 요금제(BuyTOUData)에 smpYear(연도)가 설정되어 있지 않습니다!'
        )
      }
    }
  }, [monthlyPV, BuyTOUData, PageData.cust, smpData, usingPvData, recData])

  useEffect(() => {
    if (!monthlyPV || !BuyTOUData || !shouldShowRecRevenue) {
      setMonthlyRecRevenue(undefined)
      return
    }

    const monthlySumPV = sumMonthlyLoadTime(monthlyPV)
    const revenueCustomer = PageData.cust?._id ? PageData.cust : BuyTOUData.cust

    setMonthlyRecRevenue(
      calculateMonthlyPvRecRevenue({
        customer: revenueCustomer,
        monthlyPVSum: monthlySumPV,
        recData,
        recYear: BuyTOUData.recYear,
        recRegion: BuyTOUData.recRegion,
      })
    )
  }, [monthlyPV, BuyTOUData, PageData.cust, recData, shouldShowRecRevenue])

  // ESS 사업자 거래 후 수익(afterCharge) 계산
  useEffect(() => {
    if (usingPvData.length !== 0 && BuyTOUData && holidaysData) {
      setAfterCharge(
        calMonthlyChargeAsUsage(
          usingPvData,
          BuyTOUData.touPeriodResult,
          BuyTOUData.chargeUnitResult,
          seasonMapping,
          holidaysData
        )
      )
    }
  }, [usingPvData, BuyTOUData, holidaysData])

  // ... (나머지 상태 및 UI 로직) ...

  const [selectedMonths, setSelectedMonths] = useState<number[]>([1])
  const [selectedPatternValue, setSelectedPatternValue] = useState('realTime')
  const monthNumbers = Array.from({ length: 12 }, (_, index) => index + 1)

  const toggleMonth = (index: number) => {
    if (selectedMonths.includes(index)) {
      setSelectedMonths(selectedMonths.filter((month) => month !== index))
    } else {
      setSelectedMonths([index])
    }
  }

  useEffect(() => {
    if (originPVCusList) setPVCusList(Object.values(originPVCusList)[0])
  }, [originPVCusList])

  useEffect(() => {
    if (originPurchasePlansList)
      setPurchasePlansList(Object.values(originPurchasePlansList)[0])
  }, [originPurchasePlansList])

  useEffect(() => {
    if (!PageData.cust?._id || !PageData.plans?._id) return
    if (PageData.plans.custId === PageData.cust._id) return

    setPageData((prev: any) => ({
      ...prev,
      plans: '',
    }))
    setBuyTOUData(undefined)
    setPVData(undefined)
  }, [PageData.cust?._id, PageData.plans])

  useEffect(() => {
    const custId = BuyTOUData?.cust?._id || PageData.cust?._id
    if (!custId) return
    findPVData({ variables: { val: custId } }).then((result: any) => {
      if (result.data) setPVData(Object.values(result.data)[0])
    })
  }, [BuyTOUData?.cust?._id, PageData.cust?._id])

  useEffect(() => {
    if (!PageData.plans?._id) return
    findOnePurchasePlanData({ variables: { val: PageData?.plans?._id } }).then(
      (result: any) => {
        if (result.data) {
          const planData: any = Object.values(result.data)[0]
          setBuyTOUData(planData)
          const selectedCustomer = PVCusList?.find(
            (cust: any) => cust._id === planData?.cust?._id
          )
          if (selectedCustomer) {
            setPageData((prev: any) => ({
              ...prev,
              cust: selectedCustomer,
            }))
          }
        }
      }
    )
  }, [PageData.plans?._id, PVCusList])

  return (
    <ContentWrapper>
      <Header>
        <Title>고객 경제성 분석 - PV 발전사업자</Title>
      </Header>
      <Body>
        <Left>
          <FirstRow>
            <LabelAutoComplete
              label="PV 발전사업자"
              options={PVCusList}
              value={PageData.cust}
              setData={setPageData}
              fieldName="cust"
            />
            <LabelAutoComplete
              label="요금제"
              options={filteredPurchasePlansList}
              value={PageData.plans}
              setData={setPageData}
              fieldName="plans"
            />
          </FirstRow>

          {/* 고객 요금제 표 */}
          <ItemWrapper>
            <LabelOnly label="고객 요금제" />
            <Item>
              {isSingleTimePlan ? (
                <SinglePriceBox>
                  {Number(singleUnitPrice).toLocaleString()}원/kWh
                </SinglePriceBox>
              ) : (
                <>
                  <GraphWrapper>
                    <GraphBody>
                      <TOUHeatMap
                        width="100%"
                        height="130px"
                        title="봄"
                        data={BuyTOUData?.touPeriodResult?.spr}
                      />
                      <TOUHeatMap
                        width="100%"
                        height="130px"
                        title="여름"
                        data={BuyTOUData?.touPeriodResult?.sum}
                      />
                      <TOUHeatMap
                        width="100%"
                        height="130px"
                        title="가을"
                        data={BuyTOUData?.touPeriodResult?.fall}
                      />
                      <TOUHeatMap
                        width="100%"
                        height="130px"
                        title="겨울"
                        data={BuyTOUData?.touPeriodResult?.win}
                        showLegend
                      />
                    </GraphBody>
                  </GraphWrapper>
                  <Table>
                    <TableRow>
                      <TableCell
                        style={{ borderTopLeftRadius: '4px' }}
                      ></TableCell>
                      <TableCell>봄</TableCell>
                      <TableCell>여름</TableCell>
                      <TableCell>가을</TableCell>
                      <TableCell style={{ borderTopRightRadius: '4px' }}>
                        겨울
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>경부하</TableCell>
                      <TableCell white>
                        {Number(
                          BuyTOUData?.chargeUnitResult?.spr.min
                        ).toLocaleString() ?? 0}
                      </TableCell>
                      <TableCell white>
                        {Number(
                          BuyTOUData?.chargeUnitResult?.sum.min
                        ).toLocaleString() ?? 0}
                      </TableCell>
                      <TableCell white>
                        {Number(
                          BuyTOUData?.chargeUnitResult?.fall.min
                        ).toLocaleString() ?? 0}
                      </TableCell>
                      <TableCell white>
                        {Number(
                          BuyTOUData?.chargeUnitResult?.win.min
                        ).toLocaleString() ?? 0}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>중간부하</TableCell>
                      <TableCell white>
                        {Number(
                          BuyTOUData?.chargeUnitResult?.spr.mid
                        ).toLocaleString() ?? 0}
                      </TableCell>
                      <TableCell white>
                        {Number(
                          BuyTOUData?.chargeUnitResult?.sum.mid
                        ).toLocaleString() ?? 0}
                      </TableCell>
                      <TableCell white>
                        {Number(
                          BuyTOUData?.chargeUnitResult?.fall.mid
                        ).toLocaleString() ?? 0}
                      </TableCell>
                      <TableCell white>
                        {Number(
                          BuyTOUData?.chargeUnitResult?.win.mid
                        ).toLocaleString() ?? 0}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ borderBottomLeftRadius: '4px' }}>
                        최대부하
                      </TableCell>
                      <TableCell white>
                        {Number(
                          BuyTOUData?.chargeUnitResult?.spr.max
                        ).toLocaleString() || 0}
                      </TableCell>
                      <TableCell white>
                        {Number(
                          BuyTOUData?.chargeUnitResult?.sum.max
                        ).toLocaleString() ?? 0}
                      </TableCell>
                      <TableCell white>
                        {Number(
                          BuyTOUData?.chargeUnitResult?.fall.max
                        ).toLocaleString() ?? 0}
                      </TableCell>
                      <TableCell
                        white
                        style={{ borderBottomRightRadius: '4px' }}
                      >
                        {Number(
                          BuyTOUData?.chargeUnitResult?.win.max
                        ).toLocaleString() ?? 0}
                      </TableCell>
                    </TableRow>
                  </Table>
                </>
              )}
              {BuyTOUData ? (
                <PlanSummaryText>{recContractText}</PlanSummaryText>
              ) : null}
            </Item>
          </ItemWrapper>

          {/* 태양광 발전량 표 (합계 추가됨) */}
          <ItemWrapper>
            <LabelUnit label="태양광 발전량" unit="단위: kWh" />
            <Item>
              <PvTableChartRow>
                <PvTableBox>
                  <Table>
                    <TableRow>
                      <TableCell2
                        style={{ borderTopLeftRadius: '4px' }}
                      ></TableCell2>
                      {!isSingleTimePlan ? (
                        <>
                          <TableCell2>최저가격 시간대</TableCell2>
                          <TableCell2>중간가격 시간대</TableCell2>
                          <TableCell2>최대부하 시간대</TableCell2>
                        </>
                      ) : null}
                      <TableCell2 style={{ borderTopRightRadius: '4px' }}>
                        월간 발전량
                      </TableCell2>
                    </TableRow>
                    {monthNumbers.map((month) => {
                      const monthlyTotal = monthlyPV
                        ? monthlyPV[month].min +
                          monthlyPV[month].mid +
                          monthlyPV[month].max
                        : 0

                      return (
                        <TableRow key={month}>
                          <TableCell2 white>{month}월</TableCell2>
                          {!isSingleTimePlan ? (
                            <>
                              <TableCell2 white>
                                {Number(
                                  (monthlyPV?.[month].min || 0).toFixed(2)
                                ).toLocaleString()}
                              </TableCell2>
                              <TableCell2 white>
                                {Number(
                                  (monthlyPV?.[month].mid || 0).toFixed(2)
                                ).toLocaleString()}
                              </TableCell2>
                              <TableCell2 white>
                                {Number(
                                  (monthlyPV?.[month].max || 0).toFixed(2)
                                ).toLocaleString()}
                              </TableCell2>
                            </>
                          ) : null}
                          <TableCell2 white>
                            {Number(monthlyTotal.toFixed(2)).toLocaleString()}
                          </TableCell2>
                        </TableRow>
                      )
                    })}
                    {/* [추가] 합계 행 */}
                    <TableRow>
                      <TableCell2
                        white
                        style={{
                          fontWeight: 'bold',
                          backgroundColor: '#f5f5f5',
                        }}
                      >
                        합계
                      </TableCell2>
                      {!isSingleTimePlan ? (
                        <>
                          <TableCell2
                            white
                            style={{
                              fontWeight: 'bold',
                              backgroundColor: '#f5f5f5',
                            }}
                          >
                            {Number(totalPV.min.toFixed(2)).toLocaleString()}
                          </TableCell2>
                          <TableCell2
                            white
                            style={{
                              fontWeight: 'bold',
                              backgroundColor: '#f5f5f5',
                            }}
                          >
                            {Number(totalPV.mid.toFixed(2)).toLocaleString()}
                          </TableCell2>
                          <TableCell2
                            white
                            style={{
                              fontWeight: 'bold',
                              backgroundColor: '#f5f5f5',
                            }}
                          >
                            {Number(totalPV.max.toFixed(2)).toLocaleString()}
                          </TableCell2>
                        </>
                      ) : null}
                      <TableCell2
                        white
                        style={{
                          fontWeight: 'bold',
                          backgroundColor: '#f5f5f5',
                        }}
                      >
                        {Number(totalPV.all.toFixed(2)).toLocaleString()}
                      </TableCell2>
                    </TableRow>
                  </Table>
                </PvTableBox>
                <PvChartBox>
                  <MonthlyPVBarGraph
                    width="100%"
                    height="430px"
                    data={monthlyPVBarData}
                  />
                </PvChartBox>
              </PvTableChartRow>
            </Item>
          </ItemWrapper>
        </Left>

        <Right>
          <ItemWrapper>
            <LabelUnit label="PV 발전패턴" unit="단위: kWh" />
            <Item>
              <GraphWrapper>
                <MonthChipWrapper>
                  {Array.from({ length: 12 }, (_, index) => (
                    <MonthChip
                      key={index + 1}
                      selected={selectedMonths.includes(index + 1)}
                      onClick={() => toggleMonth(index + 1)}
                    >
                      <MonthChipText>{`${index + 1}월`}</MonthChipText>
                    </MonthChip>
                  ))}
                </MonthChipWrapper>
                <GraphBody>
                  <MonthLineGraph2
                    month={selectedMonths[0]}
                    data={usingPvData ? usingPvData : []}
                    width="100%"
                    height="300px"
                    selectedChartDataType={selectedPatternValue}
                  />
                </GraphBody>
              </GraphWrapper>
            </Item>
          </ItemWrapper>

          {/* 수익 상세보기 표 (합계 추가됨) */}
          <ItemWrapper>
            <LabelUnit label="수익 상세보기" unit="단위: 원" />
            <Item>
              <GraphWrapper>
                <RevenueBarGraph
                  width="100%"
                  height="350px"
                  beforeCharge={
                    beforeCharge ? convertMonthNumToArray(beforeCharge) : []
                  }
                  afterCharge={
                    afterCharge ? convertMonthNumToArray(afterCharge) : []
                  }
                  recRevenue={
                    shouldShowRecRevenue && monthlyRecRevenue
                      ? convertMonthNumToArray(monthlyRecRevenue)
                      : undefined
                  }
                  custProfit={
                    custProfit ? convertMonthNumToArray(custProfit) : []
                  }
                />
              </GraphWrapper>
              <Table>
                <TableRow>
                  <TableCell3
                    style={{ borderTopLeftRadius: '4px' }}
                  ></TableCell3>
                  <TableCell3>기존 수익</TableCell3>
                  <TableCell3>ESS 사업자와 거래 후</TableCell3>
                  {shouldShowRecRevenue ? (
                    <TableCell3>예상 REC 수익</TableCell3>
                  ) : null}
                  <TableCell3 style={{ borderTopRightRadius: '4px' }}>
                    고객 이익
                  </TableCell3>
                </TableRow>
                {monthNumbers.map((month) => (
                  <TableRow key={month}>
                    <TableCell3 white>{month}월</TableCell3>
                    <TableCell3 white>
                      {Number(
                        beforeCharge?.[month].toFixed(0)
                      ).toLocaleString() ?? 0}
                    </TableCell3>
                    <TableCell3 white>
                      {Number(
                        afterCharge?.[month].toFixed(0)
                      ).toLocaleString() ?? 0}
                    </TableCell3>
                    {shouldShowRecRevenue ? (
                      <TableCell3 white>
                        {Number(
                          monthlyRecRevenue?.[
                            month as keyof IMonthNum
                          ]?.toFixed(0)
                        ).toLocaleString() ?? 0}
                      </TableCell3>
                    ) : null}
                    <TableCell3 white>
                      {Number(
                        custProfit?.[month].toFixed(0)
                      ).toLocaleString() ?? 0}
                    </TableCell3>
                  </TableRow>
                ))}
                {/* [추가] 합계 행 */}
                <TableRow>
                  <TableCell3
                    white
                    style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                  >
                    합계
                  </TableCell3>
                  <TableCell3
                    white
                    style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                  >
                    {Number(totalProfit.before.toFixed(0)).toLocaleString()}
                  </TableCell3>
                  <TableCell3
                    white
                    style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                  >
                    {Number(totalProfit.after.toFixed(0)).toLocaleString()}
                  </TableCell3>
                  {shouldShowRecRevenue ? (
                    <TableCell3
                      white
                      style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                    >
                      {Number(totalProfit.rec.toFixed(0)).toLocaleString()}
                    </TableCell3>
                  ) : null}
                  <TableCell3
                    white
                    style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                  >
                    {Number(totalProfit.cust.toFixed(0)).toLocaleString()}
                  </TableCell3>
                </TableRow>
              </Table>
            </Item>
          </ItemWrapper>
        </Right>
      </Body>
    </ContentWrapper>
  )
}
